import {Component, Input} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {EventClassResponse, EventResponse, ClassScoringConfigResponse, ClassPenaltyPresetResponse} from '../../../../../../../../../../@shared/models/response.module';
import {EventsService} from '../../../../../../../../../../@shared/services/events-service';
import {ScoringService} from '../../../../../../../../../../@shared/services/scoring.service';
import {_AQH_EVE_7} from '../../../../../../../../../../@shared/models/input.module';
import {firstValueFrom} from 'rxjs';

interface EventDay {
  date: Date;
  label: string;
  shortLabel: string;
}

@Component({
  selector: 'app-copy-class-modal',
  standalone: false,
  templateUrl: './copy-class-modal.html',
  styleUrl: './copy-class-modal.css'
})
export class CopyClassModal {
  @Input() eventClass: EventClassResponse;
  @Input() event: EventResponse;

  eventDays: EventDay[] = [];
  selectedDays: Date[] = [];
  copying = false;
  error: string | null = null;

  // Scoring data to copy
  originalScoringConfig: ClassScoringConfigResponse | null = null;
  originalPenaltyPresets: ClassPenaltyPresetResponse[] = [];
  loadingScoring = false;

  constructor(
    private modal: NgbActiveModal,
    private eventService: EventsService,
    private scoringService: ScoringService
  ) {}

  ngOnInit() {
    this.generateEventDays();
    this.loadScoringData();
  }

  async loadScoringData() {
    if (!this.eventClass?.event_class_id) return;

    this.loadingScoring = true;
    try {
      // Load scoring config
      this.originalScoringConfig = await firstValueFrom(
        this.scoringService.getScoringConfig({ event_class_id: this.eventClass.event_class_id })
      );

      // Load penalty presets
      this.originalPenaltyPresets = await firstValueFrom(
        this.scoringService.getPenaltyPresets({ event_class_id: this.eventClass.event_class_id })
      ) || [];
    } catch (error) {
      console.error('Error loading scoring data:', error);
    } finally {
      this.loadingScoring = false;
    }
  }

  generateEventDays() {
    if (!this.event.start_date || !this.event.end_date) {
      return;
    }

    const startDate = new Date(this.event.start_date);
    const endDate = new Date(this.event.end_date);

    this.eventDays = [];
    const currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    // Get the class's current day to mark it
    const classDate = this.eventClass.start_date_time
      ? new Date(this.eventClass.start_date_time)
      : null;

    while (currentDate <= endDate) {
      const dayDate = new Date(currentDate);
      const isSameAsClass = classDate && this.isSameDay(dayDate, classDate);

      this.eventDays.push({
        date: dayDate,
        label: this.formatDayLabel(dayDate) + (isSameAsClass ? ' (same day)' : ''),
        shortLabel: this.formatShortDayLabel(dayDate)
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  formatDayLabel(date: Date): string {
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' }).toLowerCase();
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    return `${weekday} - ${day}.${month}.${year}`;
  }

  formatShortDayLabel(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}.${month}`;
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  toggleDay(day: EventDay) {
    const index = this.selectedDays.findIndex(d => this.isSameDay(d, day.date));
    if (index >= 0) {
      this.selectedDays.splice(index, 1);
    } else {
      this.selectedDays.push(day.date);
    }
  }

  isDaySelected(day: EventDay): boolean {
    return this.selectedDays.some(d => this.isSameDay(d, day.date));
  }

  async copyClass() {
    if (this.selectedDays.length === 0) {
      return;
    }

    this.copying = true;
    this.error = null;

    try {
      // Get the original class time and date
      const originalDateTime = this.eventClass.start_date_time
        ? new Date(this.eventClass.start_date_time)
        : new Date();
      const hours = originalDateTime.getHours();
      const minutes = originalDateTime.getMinutes();

      // Extract membership plan IDs from accepted_membership_plans (allow 0 as valid ID)
      const membershipPlanIds = this.eventClass.accepted_membership_plans
        ? this.eventClass.accepted_membership_plans.map(p => p.plan_id).filter((id): id is number => id !== null && id !== undefined && id >= 0)
        : [];

      // Extract horse registry IDs from accepted_horse_registries (allow 0 as valid ID)
      const horseRegistryIds = this.eventClass.accepted_horse_registries
        ? this.eventClass.accepted_horse_registries.map(r => r.registry_id).filter((id): id is number => id !== null && id !== undefined && id >= 0)
        : [];

      // Copy to each selected day
      for (const targetDay of this.selectedDays) {
        const newDateTime = new Date(targetDay);
        newDateTime.setHours(hours, minutes, 0, 0);

        // Check if copying to the same day - add "(copy)" to name
        const isSameDay = this.isSameDay(targetDay, originalDateTime);
        const className = isSameDay
          ? `${this.eventClass.class_name} (copy)`
          : this.eventClass.class_name;

        const payload: _AQH_EVE_7 = {
          event_id: this.event.event_id,
          class_name: className,
          class_description: this.eventClass.class_description || '',
          parent_class_id: this.eventClass.parent_class_id ?? undefined,
          max_num_registrations: this.eventClass.max_num_registrations ?? undefined,
          start_date_time: this.formatDateTimeLocal(newDateTime),
          req_membership_plan_ids: membershipPlanIds,
          req_horse_registry_ids: horseRegistryIds,
          max_participant_age: this.eventClass.max_participant_age ?? undefined,
          min_horse_age: this.eventClass.min_horse_age ?? undefined,
          time_per_participant_seconds: this.eventClass.time_per_participant_seconds ?? undefined,
          price: this.eventClass.price ?? undefined,
          price_currency: this.eventClass.price_currency ?? undefined,
          vat_rate: this.eventClass.vat_rate ?? 0,
        };

        const res = await firstValueFrom(this.eventService.addEventClass(payload));
        const newEventClassId = res.data?.event_class_id;

        // Copy scoring configuration if it exists and new class was created
        if (newEventClassId && this.originalScoringConfig) {
          await firstValueFrom(this.scoringService.saveScoringConfig({
            event_class_id: newEventClassId,
            score_type: this.originalScoringConfig.score_type || 'timed',
            penalty_type: this.originalScoringConfig.penalty_type || 'time',
            time_weight: this.originalScoringConfig.time_weight ?? 1,
            points_weight: this.originalScoringConfig.points_weight ?? 1,
            decimal_places: this.originalScoringConfig.decimal_places ?? 3
          }));

          // Copy penalty presets
          for (let i = 0; i < this.originalPenaltyPresets.length; i++) {
            const preset = this.originalPenaltyPresets[i];
            await firstValueFrom(this.scoringService.addPenaltyPreset({
              event_class_id: newEventClassId,
              penalty_name: preset.penalty_name,
              penalty_value: preset.penalty_value,
              sort_order: i
            }));
          }
        }
      }

      this.copying = false;
      this.modal.close(true);
    } catch (error) {
      console.error('Error copying class:', error);
      this.error = 'Failed to copy class. Please try again.';
      this.copying = false;
    }
  }

  formatDateTimeLocal(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  close() {
    this.modal.close(false);
  }
}
